import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Tag, Empty } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TaskPhotoProofModal from './TaskPhotoProofModal';
import { composeAssignedText, composeDueText, formatLocalTime } from '../utils/taskTextComposer';

dayjs.extend(utc);
dayjs.extend(timezone);

type TaskScope = 'today' | 'range';

interface AssignedTask {
  taskId: number;
  warehouseId?: number | null;
  warehouseName: string;
  /** 接收仓 IANA 时区，展示 assignedAt/dueAt/completedAt 与判定「今日」都按此 tz */
  warehouseTimeZone?: string | null;
  title: string;
  detail?: string;
  priority: string;
  assignedAt?: string | null;
  dueAt?: string | null;
  status: string;
  isOverdue: boolean;
  overdueMinutes?: number;
  progressText?: string;
  completedAt?: string;
  requirePhotoProof: boolean;
  canCancel: boolean;
}

interface AssignedTaskListProps {
  tasks: AssignedTask[];
  dayRange: number;
  totalInRange: number;
}

const statusTag = (task: AssignedTask, label: (key: string) => string) => {
  if (task.isOverdue) {
    return <Tag color="red">{label('overdue')}</Tag>;
  }
  if (task.status === 'DONE') {
    return <Tag color="green">{label('done')}</Tag>;
  }
  if (task.status === 'IN_PROGRESS') {
    return <Tag color="orange">{label('inProgress')}</Tag>;
  }
  return <Tag>{label('notStarted')}</Tag>;
};

const AssignedTaskList: React.FC<AssignedTaskListProps> = ({
  tasks,
  dayRange,
  totalInRange,
}) => {
  const { t } = useTranslation();
  // 留证照片只在点开时拉取（预签名 URL 有时效），故按任务维度记录当前查看的是哪条
  const [photoTask, setPhotoTask] = useState<AssignedTask | null>(null);
  const [scope, setScope] = useState<TaskScope>('today');

  const statusLabel = (key: string) => String(t(`hubReview.assigned.status.${key}`));

  // 查询本身按 dayRange 回看，「今天」在前端切出来，避免为一个 Tab 多打一次上游
  // 日界按「接收仓」时区判定：派发截止时刻锚在仓 tz，日界不用仓 tz 会与仓管墙钟错位
  const isAssignedToday = (task: AssignedTask): boolean => {
    if (!task.assignedAt || !task.warehouseTimeZone) {
      return false;
    }
    const assigned = dayjs(task.assignedAt).tz(task.warehouseTimeZone);
    return assigned.isValid() && assigned.isSame(dayjs().tz(task.warehouseTimeZone), 'day');
  };

  const todayTasks = tasks.filter(isAssignedToday);
  const visibleTasks = scope === 'today' ? todayTasks : tasks;
  const overdueCount = visibleTasks.filter((task) => task.isOverdue).length;
  const tabs: Array<{ key: TaskScope; label: string }> = [
    { key: 'today', label: `${String(t('hubReview.assigned.title'))} · ${todayTasks.length}` },
    {
      key: 'range',
      label: String(t('hubReview.assigned.inRange', { days: dayRange, count: totalInRange })),
    },
  ];

  const completionLine = (task: AssignedTask): string | null => {
    if (task.status === 'DONE' && task.completedAt) {
      const time = formatLocalTime(task.completedAt, task.warehouseTimeZone) || '—';
      return `${time} ${String(t('hubReview.assigned.completed'))}`;
    }
    if (task.isOverdue && task.overdueMinutes) {
      const hours = Math.floor(task.overdueMinutes / 60);
      const overdueText = hours > 0
        ? String(t('hubReview.assigned.overdueHours', { count: hours }))
        : String(t('hubReview.assigned.overdueMinutes', { count: task.overdueMinutes }));
      return task.progressText ? `${task.progressText} · ${overdueText}` : overdueText;
    }
    return task.progressText ?? null;
  };

  return (
    <Card className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-end gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setScope(tab.key)}
              className={`text-sm pb-1 border-b-2 ${
                scope === tab.key
                  ? 'font-semibold text-gray-900 border-violet-600'
                  : 'text-gray-500 border-transparent'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {overdueCount > 0 && (
          <span className="text-xs text-red-500">
            {String(t('hubReview.assigned.overdueCount', { count: overdueCount }))}
          </span>
        )}
      </div>

      {visibleTasks.length > 0 ? (
        <div className="space-y-3">
          {visibleTasks.map((task) => (
            <div
              key={task.taskId}
              className={`p-3 rounded border ${
                task.isOverdue ? 'border-red-200 bg-red-50' : 'border-violet-200'
              }`}
            >
              <div className="flex justify-between items-start gap-2 mb-1">
                {/* 标题可能出现连续无 break 机会的字符（如 。。。 或长英文单词），
                    需 min-w-0 + break-all 才能让 flex 子项收缩、把状态标签挤回边框内 */}
                <span className="text-sm font-medium flex-1 min-w-0 break-all">
                  {task.title}
                  {/* 口径同仓管工作台的任务行：已完结才可能有留证，未完结只出灰图标示意需拍照 */}
                  {task.requirePhotoProof && (
                    task.status === 'DONE' ? (
                      <button
                        type="button"
                        className="ml-1.5 text-violet-600"
                        title={String(t('workbench.viewPhotoProof'))}
                        onClick={() => setPhotoTask(task)}
                      >
                        <CameraOutlined />
                      </button>
                    ) : (
                      <CameraOutlined
                        className="ml-1.5 text-gray-400"
                        title={String(t('workbench.photoRequired'))}
                      />
                    )
                  )}
                </span>
                <span className="shrink-0">{statusTag(task, statusLabel)}</span>
              </div>
              <p className="text-xs text-gray-500">
                {[
                  task.warehouseName,
                  composeAssignedText(task.assignedAt, task.warehouseTimeZone),
                  composeDueText(task.dueAt, task.warehouseTimeZone),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {completionLine(task) && (
                <p className="text-xs text-gray-600 mt-1">{completionLine(task)}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={String(t('hubReview.assigned.empty'))}
        />
      )}

      {photoTask && (
        <TaskPhotoProofModal
          open
          readOnly
          taskTitle={photoTask.title}
          requirePhotoProof={photoTask.requirePhotoProof}
          warehouseId={photoTask.warehouseId ?? undefined}
          taskId={photoTask.taskId}
          onCancel={() => setPhotoTask(null)}
          onConfirm={() => setPhotoTask(null)}
        />
      )}
    </Card>
  );
};

export default AssignedTaskList;
